import React from 'react';
import { MainLayout } from '../layouts/MainLayout';
import { Card } from '../components/ui/Card';
import { MetricCard } from '../components/dashboard/MetricCard';
import { ActivityFeed } from '../components/dashboard/ActivityFeed';
import { Activity } from '../types';
import { Server, Ticket, ShoppingCart, TrendingUp, AlertTriangle, CheckCircle, DollarSign, TrendingDown, Users, Clock, Target, Zap } from 'lucide-react';
interface DashboardProps {
  onLogout: () => void;
}
const mockActivities: Activity[] = [{
  id: '1',
  type: 'ticket',
  message: 'Neues Ticket #1042 von Zahnarztpraxis Dr. Weber erstellt',
  timestamp: 'vor 5 Min',
  user: 'System'
}, {
  id: '2',
  type: 'system',
  message: 'Server SRV-DC-01 erfolgreich aktualisiert',
  timestamp: 'vor 23 Min',
  user: 'Admin'
}, {
  id: '3',
  type: 'order',
  message: 'Bestellung #ORD-2023-005 bei Ingram aufgegeben',
  timestamp: 'vor 1 Std',
  user: 'Max Mustermann'
}, {
  id: '4',
  type: 'security',
  message: 'Firewall-Regel für Kanzlei Schmidt & Partner aktualisiert',
  timestamp: 'vor 2 Std',
  user: 'Admin'
}, {
  id: '5',
  type: 'ticket',
  message: 'Ticket #1038 wurde geschlossen',
  timestamp: 'vor 3 Std',
  user: 'Lisa Schmidt'
}];
export function Dashboard({
  onLogout
}: DashboardProps) {
  return <MainLayout title="Dashboard" onLogout={onLogout} data-id="element-552">
      {/* IT Operations Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-553">
        <MetricCard title="Aktive Assets" value="247" change="+12%" trend="up" icon={<Server className="h-5 w-5" data-id="element-555" />} data-id="element-554" />
        <MetricCard title="Offene Tickets" value="18" change="-23%" trend="down" icon={<Ticket className="h-5 w-5" data-id="element-557" />} data-id="element-556" />
        <MetricCard title="Bestellungen" value="34" change="+8%" trend="up" icon={<ShoppingCart className="h-5 w-5" data-id="element-559" />} data-id="element-558" />
        <MetricCard title="System Health" value="98.2%" change="+0.3%" trend="up" icon={<TrendingUp className="h-5 w-5" data-id="element-561" />} data-id="element-560" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-562">
        <Card title="Kritische Warnungen" data-id="element-563">
          <div className="space-y-4" data-id="element-564">
            <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100" data-id="element-565">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" data-id="element-566" />
              <div data-id="element-567">
                <h4 className="font-medium text-red-900" data-id="element-568">
                  Firewall Update ausstehend
                </h4>
                <p className="text-sm text-red-700 mt-1" data-id="element-569">
                  Kritisches Sicherheitsupdate für 3 Firewalls verfügbar
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100" data-id="element-570">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" data-id="element-571" />
              <div data-id="element-572">
                <h4 className="font-medium text-amber-900" data-id="element-573">
                  Backup-Fehler erkannt
                </h4>
                <p className="text-sm text-amber-700 mt-1" data-id="element-574">
                  Letztes Backup für Server-03 fehlgeschlagen
                </p>
              </div>
            </div>
          </div>
        </Card>

        <ActivityFeed activities={mockActivities} data-id="element-575" />
      </div>

      <Card title="System Status" data-id="element-576">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-577">
          <div className="text-center p-4 bg-emerald-50 rounded-lg" data-id="element-578">
            <CheckCircle className="h-8 w-8 text-emerald-600 mx-auto mb-2" data-id="element-579" />
            <div className="text-2xl font-bold text-emerald-900" data-id="element-580">234</div>
            <div className="text-sm text-emerald-700" data-id="element-581">Online</div>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-lg" data-id="element-582">
            <Server className="h-8 w-8 text-slate-600 mx-auto mb-2" data-id="element-583" />
            <div className="text-2xl font-bold text-slate-900" data-id="element-584">13</div>
            <div className="text-sm text-slate-700" data-id="element-585">Wartung</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg" data-id="element-586">
            <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-2" data-id="element-587" />
            <div className="text-2xl font-bold text-red-900" data-id="element-588">3</div>
            <div className="text-sm text-red-700" data-id="element-589">Offline</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg" data-id="element-590">
            <TrendingUp className="h-8 w-8 text-blue-600 mx-auto mb-2" data-id="element-591" />
            <div className="text-2xl font-bold text-blue-900" data-id="element-592">98.2%</div>
            <div className="text-sm text-blue-700" data-id="element-593">Uptime</div>
          </div>
        </div>
      </Card>

      {/* Financial Metrics Section - Moved to Bottom */}
      <Card title="Finanzkennzahlen" data-id="element-594">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-595">
          {/* Umsatzrentabilität */}
          <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg border border-emerald-100" data-id="element-596">
            <div className="flex items-center justify-between mb-3" data-id="element-597">
              <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-598">
                <TrendingUp className="h-5 w-5 text-emerald-600" data-id="element-599" />
              </div>
              <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 px-2 py-1 rounded" data-id="element-600">
                +4.2%
              </span>
            </div>
            <h3 className="text-sm font-medium text-slate-600 mb-1" data-id="element-601">
              Umsatzrentabilität
            </h3>
            <p className="text-2xl font-bold text-slate-900" data-id="element-602">18.5%</p>
            <p className="text-xs text-slate-500 mt-1" data-id="element-603">
              € 185k Gewinn / € 1M Umsatz
            </p>
          </div>

          {/* Cashflow */}
          <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border border-blue-100" data-id="element-604">
            <div className="flex items-center justify-between mb-3" data-id="element-605">
              <div className="p-2 bg-blue-100 rounded-lg" data-id="element-606">
                <DollarSign className="h-5 w-5 text-blue-600" data-id="element-607" />
              </div>
              <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded" data-id="element-608">
                +12.8%
              </span>
            </div>
            <h3 className="text-sm font-medium text-slate-600 mb-1" data-id="element-609">
              Cashflow (Monat)
            </h3>
            <p className="text-2xl font-bold text-slate-900" data-id="element-610">€ 142k</p>
            <p className="text-xs text-slate-500 mt-1" data-id="element-611">
              Operativer Cashflow positiv
            </p>
          </div>

          {/* Customer Acquisition Cost */}
          <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100" data-id="element-612">
            <div className="flex items-center justify-between mb-3" data-id="element-613">
              <div className="p-2 bg-purple-100 rounded-lg" data-id="element-614">
                <Users className="h-5 w-5 text-purple-600" data-id="element-615" />
              </div>
              <span className="text-xs font-semibold text-purple-600 bg-purple-100 px-2 py-1 rounded" data-id="element-616">
                -8.5%
              </span>
            </div>
            <h3 className="text-sm font-medium text-slate-600 mb-1" data-id="element-617">CAC</h3>
            <p className="text-2xl font-bold text-slate-900" data-id="element-618">€ 1,240</p>
            <p className="text-xs text-slate-500 mt-1" data-id="element-619">
              Customer Acquisition Cost
            </p>
          </div>

          {/* Customer Lifetime Value */}
          <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-100" data-id="element-620">
            <div className="flex items-center justify-between mb-3" data-id="element-621">
              <div className="p-2 bg-amber-100 rounded-lg" data-id="element-622">
                <Target className="h-5 w-5 text-amber-600" data-id="element-623" />
              </div>
              <span className="text-xs font-semibold text-amber-600 bg-amber-100 px-2 py-1 rounded" data-id="element-624">
                +15.2%
              </span>
            </div>
            <h3 className="text-sm font-medium text-slate-600 mb-1" data-id="element-625">CLV</h3>
            <p className="text-2xl font-bold text-slate-900" data-id="element-626">€ 18,500</p>
            <p className="text-xs text-slate-500 mt-1" data-id="element-627">
              Customer Lifetime Value
            </p>
          </div>
        </div>

        {/* Additional Financial Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6" data-id="element-628">
          {/* IT-Kosten pro Nutzer */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-629">
            <div className="flex items-center gap-3 mb-3" data-id="element-630">
              <div className="p-2 bg-slate-200 rounded-lg" data-id="element-631">
                <DollarSign className="h-4 w-4 text-slate-600" data-id="element-632" />
              </div>
              <div className="flex-1" data-id="element-633">
                <h3 className="text-sm font-medium text-slate-600" data-id="element-634">
                  IT-Kosten pro Nutzer
                </h3>
                <p className="text-xl font-bold text-slate-900" data-id="element-635">€ 142/Monat</p>
              </div>
              <span className="text-xs font-medium text-emerald-600" data-id="element-636">
                -3.2%
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-637">
              <div className="bg-teal-500 h-2 rounded-full" style={{
              width: '68%'
            }} data-id="element-638"></div>
            </div>
            <p className="text-xs text-slate-500 mt-2" data-id="element-639">
              68% unter Branchendurchschnitt
            </p>
          </div>

          {/* SLA Einhaltung */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-640">
            <div className="flex items-center gap-3 mb-3" data-id="element-641">
              <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-642">
                <CheckCircle className="h-4 w-4 text-emerald-600" data-id="element-643" />
              </div>
              <div className="flex-1" data-id="element-644">
                <h3 className="text-sm font-medium text-slate-600" data-id="element-645">
                  SLA Einhaltung
                </h3>
                <p className="text-xl font-bold text-slate-900" data-id="element-646">99.4%</p>
              </div>
              <span className="text-xs font-medium text-emerald-600" data-id="element-647">
                +0.8%
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-648">
              <div className="bg-emerald-500 h-2 rounded-full" style={{
              width: '99.4%'
            }} data-id="element-649"></div>
            </div>
            <p className="text-xs text-slate-500 mt-2" data-id="element-650">
              247 von 249 SLAs eingehalten
            </p>
          </div>

          {/* Incident Management Effizienz */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-651">
            <div className="flex items-center gap-3 mb-3" data-id="element-652">
              <div className="p-2 bg-blue-100 rounded-lg" data-id="element-653">
                <Zap className="h-4 w-4 text-blue-600" data-id="element-654" />
              </div>
              <div className="flex-1" data-id="element-655">
                <h3 className="text-sm font-medium text-slate-600" data-id="element-656">
                  Incident Resolution
                </h3>
                <p className="text-xl font-bold text-slate-900" data-id="element-657">2.4h</p>
              </div>
              <span className="text-xs font-medium text-emerald-600" data-id="element-658">-18%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-659">
              <div className="bg-blue-500 h-2 rounded-full" style={{
              width: '82%'
            }} data-id="element-660"></div>
            </div>
            <p className="text-xs text-slate-500 mt-2" data-id="element-661">
              Ø Lösungszeit für Incidents
            </p>
          </div>
        </div>

        {/* CLV/CAC Ratio Highlight */}
        <div className="mt-6 p-4 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-lg text-white" data-id="element-662">
          <div className="flex items-center justify-between" data-id="element-663">
            <div data-id="element-664">
              <h3 className="text-sm font-medium text-teal-100 mb-1" data-id="element-665">
                CLV/CAC Ratio
              </h3>
              <p className="text-3xl font-bold" data-id="element-666">14.9:1</p>
              <p className="text-sm text-teal-100 mt-1" data-id="element-667">
                Hervorragende Kundenrentabilität (Ziel: &gt;3:1)
              </p>
            </div>
            <div className="text-right" data-id="element-668">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-2 rounded-lg" data-id="element-669">
                <TrendingUp className="h-5 w-5" data-id="element-670" />
                <span className="text-lg font-bold" data-id="element-671">+22%</span>
              </div>
              <p className="text-xs text-teal-100 mt-2" data-id="element-672">vs. Vorquartal</p>
            </div>
          </div>
        </div>
      </Card>
    </MainLayout>;
}